'use client'

import Image from 'next/image'
import Link from 'next/link'
import { BadgeCheck, Check, ExternalLink, Lock, Ticket, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  formatMoney,
  formatPrizePool,
  formatDate,
  formatTime,
  isGamerPlugHosted,
  relativeDay,
  tournamentSourceLabel,
  type Tournament,
} from '@/lib/tournaments'
import { ctaFor, trackExternalTournamentClick } from './detail-common'
import { UserAvatar } from '@/components/ui/user-avatar'

/**
 * The tournament card used on both tournament pages: the public /tournaments
 * calendar (in a grid under each day) and the app's Tournaments section.
 *
 * Short cover with only who hosts it ("GamerPlug event" in red, or "via
 * Challengermode" with a greyed cover), then date, title and organizer, then
 * a footer: fee chip, prize and spots on the left, the register action on the
 * right, and the fill bar along the very bottom.
 *
 * The action (unless `showRegister` is false): "Register ↗" goes straight to
 * an imported event's organizer site; "Join" opens a GamerPlug event's page
 * with ?join=1, where the full join flow starts by itself; "Registered" when
 * you're already in.
 */
export function TournamentCard({
  t,
  locale,
  displayTz,
  showRegister = true,
  registered = false,
}: {
  t: Tournament
  locale: string
  /** Timezone to show times in (the viewer's); falls back to the event's. */
  displayTz?: string
  showRegister?: boolean
  /** The viewer is already registered (shows "Registered" instead of Join). */
  registered?: boolean
}) {
  const hosted = isGamerPlugHosted(t)
  const canRegister = showRegister && t.status !== 'completed'

  return (
    <div
      className={cn(
        'relative flex flex-col overflow-hidden rounded-2xl border bg-card transition-colors duration-300',
        hosted
          ? 'border-primary/45 shadow-lg shadow-primary/10 hover:border-primary/70'
          : 'border-border hover:border-white/20'
      )}
    >
      <TileBody t={t} locale={locale} displayTz={displayTz} hosted={hosted} />
      {/* Outside the card link, so the action isn't a link inside a link */}
      <div className="px-4 pb-4">
        <div className="flex items-center gap-3 border-t border-white/5 pt-3">
          <span className="min-w-0 flex-1 text-xs tabular-nums text-muted-foreground">
            <TileMeta t={t} hosted={hosted} />
          </span>
          {canRegister && <TileAction t={t} locale={locale} registered={registered} />}
        </div>
        {/* Fill bar along the bottom, below everything (capped events only) */}
        {t.maxParticipants > 0 && (
          <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/[0.06]">
            <div
              className={cn('h-full rounded-full', hosted ? 'bg-primary' : 'bg-white/40')}
              style={{
                width: `${t.registered > 0 ? Math.max(4, Math.min(100, (t.registered / t.maxParticipants) * 100)) : 0}%`,
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

/** "GamerPlug event" or "via Challengermode". */
function HostBadge({ t, hosted, overlay = false }: { t: Tournament; hosted: boolean; overlay?: boolean }) {
  if (hosted) {
    return (
      <span className="inline-flex items-center rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-white shadow-md shadow-primary/30">
        GamerPlug event
      </span>
    )
  }
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
        overlay
          ? 'border-white/15 bg-black/55 text-white/80 backdrop-blur-sm'
          : 'border-white/10 bg-white/5 text-muted-foreground'
      )}
    >
      via {tournamentSourceLabel(t.source!)}
    </span>
  )
}

function Cover({ t, hosted, sizes, className }: { t: Tournament; hosted: boolean; sizes: string; className?: string }) {
  const dim = !hosted && 'opacity-60 grayscale'
  return t.coverImage ? (
    <Image src={t.coverImage} alt="" fill sizes={sizes} className={cn('object-cover object-center', dim, className)} />
  ) : (
    <div
      className={cn('absolute inset-0', dim)}
      style={{ background: `linear-gradient(135deg, ${t.accent.from}, ${t.accent.to})` }}
    />
  )
}

/** "Today" / "Tomorrow", else "Thu, Nov 5", in the given zone. */
function dayLabel(iso: string, tz?: string): string {
  const rel = relativeDay(iso, tz)
  if (rel === 'Today' || rel === 'Tomorrow') return rel
  return formatDate(iso, { weekday: 'short' }, tz)
}

/** Fee chip, prize, and "0/40" spots, as on the original cards. */
function TileMeta({ t, hosted }: { t: Tournament; hosted: boolean }) {
  const capped = t.maxParticipants > 0
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white/[0.06] px-2 py-0.5">
        <Ticket className="size-3" />
        {formatMoney(t.entryFeeCents)}
      </span>
      {t.prizePoolCents > 0 && (
        <span className={cn('truncate font-semibold', hosted ? 'text-primary' : 'text-foreground/85')}>
          {formatPrizePool(t.prizePoolCents)} prize
        </span>
      )}
      <span className="inline-flex shrink-0 items-center gap-1">
        <Users className="size-3" />
        {capped ? `${t.registered}/${t.maxParticipants}` : `${t.registered} joined`}
      </span>
    </span>
  )
}

/** Tile action: a quiet "Register ↗" link, a red "Join" button, or "Registered". */
function TileAction({ t, locale, registered }: { t: Tournament; locale: string; registered: boolean }) {
  const cta = ctaFor(t)
  if (cta.kind === 'external' && cta.href) {
    return (
      <a
        href={cta.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackExternalTournamentClick(t, 'list_card', cta.href)}
        className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-foreground transition-colors hover:text-primary"
      >
        Register
        <ExternalLink className="size-3.5" />
      </a>
    )
  }
  if (registered) {
    return (
      <Link
        href={`/${locale}/tournaments/${t.slug}`}
        className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-emerald-400 hover:text-emerald-300"
      >
        <Check className="size-4" />
        Registered
      </Link>
    )
  }
  return (
    <Link
      href={`/${locale}/tournaments/${t.slug}?join=1`}
      className="inline-flex h-8 shrink-0 items-center rounded-lg bg-primary px-3.5 text-sm font-bold text-white transition-colors hover:bg-primary/90"
    >
      Join
    </Link>
  )
}

function TileBody({
  t,
  locale,
  displayTz,
  hosted,
}: {
  t: Tournament
  locale: string
  displayTz?: string
  hosted: boolean
}) {
  const tz = displayTz ?? t.timezone
  return (
    <Link href={`/${locale}/tournaments/${t.slug}`} className="group flex flex-1 flex-col">
      {/* Short cover: art plus who hosts it, nothing else */}
      <div className="relative aspect-[16/6] w-full overflow-hidden">
        <Cover
          t={t}
          hosted={hosted}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 320px"
          className="transition-transform duration-500 group-hover:scale-[1.03]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent" />
        <div className="absolute left-3 top-3 flex items-center gap-1.5">
          <HostBadge t={t} hosted={hosted} overlay />
          {t.visibility === 'private' && (
            <span className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-black/50 px-2 py-0.5 text-xs font-semibold text-white backdrop-blur-sm">
              <Lock className="size-3" />
              Private
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-1.5 px-4 pb-4 pt-3">
        <div className="text-xs tabular-nums text-muted-foreground">
          <span className="font-semibold text-foreground">{dayLabel(t.startsAt, tz)}</span> · {formatTime(t.startsAt, tz)}
        </div>
        <h3 className="line-clamp-2 font-semibold leading-snug">{t.title}</h3>
        <OrganizerName t={t} />
      </div>
    </Link>
  )
}

/** Organizer under the tile's title: hosts' avatars + names, or the org name, plus a check if verified. */
function OrganizerName({ t }: { t: Tournament }) {
  return (
    <div className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
      {t.organizers.length > 0 ? (
        <>
          <div className="flex shrink-0 items-center -space-x-1.5">
            {t.organizers.map((o) => (
              <UserAvatar key={o.id} url={o.avatar} alt={o.gamertag} size={18} className="ring-1 ring-card" />
            ))}
          </div>
          <span className="truncate font-medium text-foreground/85">
            {t.organizers.map((o) => o.gamertag).join(', ')}
          </span>
        </>
      ) : (
        <span className="truncate font-medium text-foreground/85">{t.organizer.name}</span>
      )}
      {t.organizers.length === 0 && t.organizer.verified && (
        <BadgeCheck className="size-4 shrink-0 text-sky-400" aria-label="Verified organizer" />
      )}
    </div>
  )
}
